import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { PasswordValidationService } from '@services/password-validation.service';

@Component({
  selector: 'app-user-reset',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly passwordValidation = inject(PasswordValidationService);

  resetForm!: FormGroup;
  token: string | null = null;

  tokenInvalid = false;
  isSubmitting = false;
  passwordReset = false;
  showPassword = false;
  showConfirmPassword = false;
  errorMessage: string | null = null;
  rotating = false;

  private destroy$ = new Subject<void>();

  get statusIcon(): string {
    if (this.isSubmitting) return 'sync';
    if (this.passwordReset) return 'check_circle';
    return 'lock_reset';
  }

  get statusLabel(): string {
    if (this.isSubmitting) return 'Réinitialisation en cours...';
    if (this.passwordReset) return 'Mot de passe réinitialisé';
    return 'Réinitialiser le mot de passe';
  }

  get isButtonDisabled(): boolean {
    return (
      this.resetForm.invalid ||
      this.isSubmitting ||
      this.passwordReset ||
      this.tokenInvalid
    );
  }

  ngOnInit(): void {
    this.initForm();
    this.extractToken();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.resetForm = this.fb.group(
      {
        password: [
          '',
          [
            Validators.required,
            this.passwordValidation.passwordStrengthValidator(),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordValidation.passwordMatchValidator() }
    );

    this.resetForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.errorMessage) {
        this.errorMessage = null;
        this.cdr.markForCheck();
      }
    });
  }

  private extractToken(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.token = params.get('token');
      if (!this.token) {
        this.errorMessage = 'Token de réinitialisation manquant ou invalide.';
        this.cdr.markForCheck();
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
    this.cdr.markForCheck();
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
    this.cdr.markForCheck();
  }

  hasError(field: string, error: string): boolean {
    return this.passwordValidation.hasFieldError(this.resetForm, field, error);
  }

  hasFormError(error: string): boolean {
    return this.passwordValidation.hasFormError(
      this.resetForm,
      error,
      'confirmPassword'
    );
  }

  getPasswordErrors(): string[] {
    return this.passwordValidation.getPasswordErrors(
      this.resetForm.get('password')
    );
  }

  onSubmit(): void {
    this.resetForm.markAllAsTouched();

    if (this.resetForm.invalid || !this.token) {
      this.cdr.markForCheck();
      return;
    }

    const password = this.resetForm.get('password')?.value;

    if (!password || !this.token) {
      return;
    }

    this.tokenInvalid = false;
    this.isSubmitting = true;
    this.rotating = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.authService
      .resetPassword({ token: this.token, password: password })
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.rotating = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          if (response.success === true) {
            this.passwordReset = true;
            this.resetForm.disable();
            this.cdr.markForCheck();
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          } else {
            // Token invalide ou expiré
            this.tokenInvalid = true;
            this.resetForm.disable();
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.tokenInvalid = true;
          this.resetForm.disable();
          this.cdr.markForCheck();
        },
      });
  }

  /** Redirige vers la page de demande de reset */
  requestNewResetLink(): void {
    this.router.navigate(['/reset-password/request']);
  }
}
